import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, KeyRound, X, Loader2, UserRound, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const ROLE_FILTERS = ["all", "customer", "doctor"];

function PasswordDialog({ target, onClose, onSaved }) {
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    async function save(e) {
        e.preventDefault();
        setErr("");
        if (newPw.length < 6) {
            setErr("Password must be at least 6 characters.");
            return;
        }
        if (newPw !== confirmPw) {
            setErr("Passwords do not match.");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/admin/users/${target.user_id}/password`, {
                new_password: newPw,
            });
            toast.success(`Password updated for ${target.name}.`);
            onSaved();
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Could not update password.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            data-testid={TID.adminUsersPwDialog}
            className="fixed inset-0 z-40 flex items-end bg-black/40 md:items-center md:justify-center"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md border border-veda-border bg-veda-surface p-6"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                            Reset password
                        </p>
                        <h3 className="font-display mt-1 text-xl text-veda-onSurface">
                            {target.name}
                        </h3>
                        <p className="text-xs text-veda-muted">
                            {target.email} · {target.role}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid={TID.adminUsersPwCancel}
                        className="p-1.5 text-veda-muted"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <form onSubmit={save} className="mt-5 flex flex-col gap-3">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        New password
                    </label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={newPw}
                        data-testid={TID.adminUsersPwNew}
                        onChange={(e) => setNewPw(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Confirm new password
                    </label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={confirmPw}
                        data-testid={TID.adminUsersPwConfirm}
                        onChange={(e) => setConfirmPw(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />
                    {err && (
                        <p className="text-xs text-veda-error">{err}</p>
                    )}
                    <div className="mt-2 flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="border border-veda-border bg-veda-surface px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid={TID.adminUsersPwSubmit}
                            className="inline-flex items-center gap-2 bg-veda-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-70"
                        >
                            {saving && <Loader2 size={12} className="animate-spin" />}
                            Save new password
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [role, setRole] = useState("all");
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch {
            toast.error("Could not load users.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return users
            .filter((u) => u.role === "customer" || u.role === "doctor")
            .filter((u) => (role === "all" ? true : u.role === role))
            .filter((u) =>
                term
                    ? (u.name || "").toLowerCase().includes(term) ||
                      (u.email || "").toLowerCase().includes(term)
                    : true,
            );
    }, [users, q, role]);

    return (
        <div data-testid={TID.adminUsers} className="flex flex-col gap-6">
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Users
                </p>
                <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                    Customers &amp; Vaidyas
                </h2>
                <p className="mt-2 text-sm text-veda-muted">
                    Reset a password for any customer or doctor account. Admin
                    accounts can only be changed from the admin's own settings.
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-1 items-center border border-veda-border bg-veda-surface">
                    <Search size={14} className="ml-3 text-veda-muted" />
                    <input
                        type="search"
                        placeholder="Search by name or email"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid={TID.adminUsersSearch}
                        className="flex-1 bg-transparent px-3 py-2.5 text-sm text-veda-onSurface placeholder:text-veda-muted focus:outline-none"
                    />
                </div>
                <div className="flex gap-2">
                    {ROLE_FILTERS.map((r) => (
                        <button
                            key={r}
                            onClick={() => setRole(r)}
                            data-testid={TID.adminUsersRoleChip(r)}
                            className={`rounded-pill border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                role === r
                                    ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                    : "border-veda-border bg-veda-surface text-veda-onSurface"
                            }`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="border border-veda-border bg-veda-surface2 p-10 text-center text-sm text-veda-muted">
                    Loading users…
                </div>
            ) : filtered.length === 0 ? (
                <div className="border border-veda-border bg-veda-surface2 p-10 text-center text-sm text-veda-muted">
                    No users match your filter.
                </div>
            ) : (
                <ul className="divide-y divide-veda-divider border border-veda-border bg-veda-surface">
                    {filtered.map((u) => (
                        <li
                            key={u.user_id}
                            data-testid={TID.adminUserRow(u.user_id)}
                            className="flex items-center gap-3 px-4 py-3"
                        >
                            <span
                                className={`grid h-9 w-9 place-items-center ${
                                    u.role === "doctor"
                                        ? "bg-veda-brand/10 text-veda-brand"
                                        : "bg-veda-surface2 text-veda-onSurface"
                                }`}
                            >
                                {u.role === "doctor" ? (
                                    <Stethoscope size={15} />
                                ) : (
                                    <UserRound size={15} />
                                )}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-veda-onSurface">
                                    {u.name}
                                </p>
                                <p className="truncate text-xs text-veda-muted">
                                    {u.email} · {u.role}
                                </p>
                            </div>
                            <button
                                onClick={() => setTarget(u)}
                                data-testid={TID.adminUserResetPw(u.user_id)}
                                className="inline-flex items-center gap-1.5 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface hover:bg-veda-surface2"
                            >
                                <KeyRound size={11} />
                                Reset password
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {target && (
                <PasswordDialog
                    target={target}
                    onClose={() => setTarget(null)}
                    onSaved={() => setTarget(null)}
                />
            )}
        </div>
    );
}
